import { Button, type ButtonProps } from "@/components/ui/button";
import { ResponsiveLabel } from "@/components/ResponsiveLabel";
import { decodeHtmlEntities } from "@/utilities/decodeHtmlEntities";
import { cn } from "@/utilities/ui";
import Link from "next/link";
import React from "react";
import { ArrowRight } from "lucide-react";

import type { Page, News } from "@/payload-types";

type CMSLinkType = {
  appearance?: "inline" | "underline" | ButtonProps["variant"];
  children?: React.ReactNode;
  className?: string;
  colorMode?: ButtonProps["colorMode"];
  label?: string | null;
  mobileLabel?: string | null;
  newTab?: boolean | null;
  reference?: {
    relationTo: "pages" | "news";
    value: Page | News | string | number;
  } | null;
  size?: ButtonProps["size"] | null;
  type?: "custom" | "reference" | null;
  url?: string | null;
};

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = "inline",
    children,
    className,
    colorMode = "light",
    label,
    mobileLabel,
    newTab,
    reference,
    size: sizeFromProps,
    url,
  } = props;

  const href =
    type === "reference" &&
    typeof reference?.value === "object" &&
    reference.value.slug
      ? `${reference?.relationTo !== "pages" ? `/${reference?.relationTo}` : ""}/${
          reference.value.slug
        }`
      : url;

  if (!href) return null;

  const resolvedAppearance: ButtonProps["variant"] =
    appearance === "default"
      ? "secondary"
      : (appearance as ButtonProps["variant"]);
  const size =
    sizeFromProps ??
    (appearance === "default" || appearance === "outline" ? "xl" : "default");
  const decodedLabel = decodeHtmlEntities(label) ?? label;
  const decodedMobileLabel = decodeHtmlEntities(mobileLabel) ?? mobileLabel;
  const hasLabel = Boolean(decodedLabel || decodedMobileLabel);
  const renderedChildren =
    typeof children === "string"
      ? (decodeHtmlEntities(children) ?? children)
      : children;
  const newTabProps = newTab
    ? { rel: "noopener noreferrer", target: "_blank" }
    : {};

  /* Ensure we don't break any styles set by richText */
  if (appearance === "inline") {
    return (
      <Link className={cn(className)} href={href || url || ""} {...newTabProps}>
        {hasLabel ? (
          <ResponsiveLabel
            label={decodedLabel}
            mobileLabel={decodedMobileLabel}
          />
        ) : null}
        {renderedChildren && renderedChildren}
      </Link>
    );
  }

  if (appearance === "underline") {
    return (
      <Button
        asChild
        className={className}
        colorMode={colorMode}
        size="link-lg"
        variant="link"
      >
        <Link href={href || url || ""} {...newTabProps}>
          {hasLabel ? (
            <ResponsiveLabel
              label={decodedLabel}
              mobileLabel={decodedMobileLabel}
            />
          ) : null}
          {renderedChildren ?? (
            <ArrowRight
              className="transition-transform duration-200 group-hover:translate-x-1 group-hover/button:translate-x-1"
              aria-hidden="true"
            />
          )}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      asChild
      className={className}
      colorMode={colorMode}
      size={size}
      variant={resolvedAppearance}
    >
      <Link className={cn(className)} href={href || url || ""} {...newTabProps}>
        {hasLabel ? (
          <ResponsiveLabel
            label={decodedLabel}
            mobileLabel={decodedMobileLabel}
          />
        ) : null}
        {renderedChildren && renderedChildren}
      </Link>
    </Button>
  );
};
